/**
 * Only for y4 common api
 */

import axios from 'axios';
import RT from 'y4/RT';
import { v4 as uuidv4 } from 'uuid';
import { clearAuthentication } from './Authentication';
import is from 'is_js';
import y from './Utils';

/** Can call all y4 apis
 * @param {*} url
 * @param {*} params
 * @returns RT
 */
async function callY4Api(url, params) {
  try {
    if (!params) params = {};
    params.path = window.location.pathname;
    const response = await axios.post(
      process.env.REACT_APP_API_ROOT + url,
      params
    );
    return checkY4Result(new RT(response));
  } catch (error) {
    return new RT(error);
  }
}

/** Call y4 servlet, based on function callY4Api
 * @param {*} servlet
 * @param {*} command
 * @param {*} params
 * @returns
 */
async function callY4Servlet(servlet, command, params) {
  var ps = {
    servlet: servlet,
    command: command,
    args: [JSON.stringify(params), 'simple']
  };
  return callY4Api('/Y4/CallServlet', ps);
}

/**
 * Log into to server
 * @param {*} content
 * @returns
 */
async function logToY4(flag, content) {
  const c =
    is.object(content) || is.array(content) ? JSON.stringify(content) : content;
  return callY4Api('/Y4/Log', {
    content: `${flag} -> ${c}`
  });
}

/**
 *
 * @param {*} queries string or object
 * @returns
 */
function getSearchFromQueries(queries) {
  if (!queries) return '';
  if (is.string(queries)) return `&${queries}`;
  const qsp = new URLSearchParams(queries);
  var s = qsp.toString();
  if (s) return `&${s}`;
  return '';
}

/**
 * Post data to y4 api
 * @param {*} url url
 * @param {*} json In data
 * @param {*} config In data
 */
async function postToY4Api(servlet, command, params, queries, config = null) {
  try {
    var oqs = getSearchFromQueries(queries);
    if (!params) params = {};
    params.path = window.location.pathname;
    const response = await axios.post(
      process.env.REACT_APP_API_ROOT +
        `/Y4/PostToServlet?servlet=${servlet}&command=${command}${oqs}`,
      params,
      config
    );
    return checkY4Result(new RT(response));
  } catch (error) {
    return new RT(error);
  }
}

/**
 * Put data to y4 api
 * @param {*} command
 * @param {*} params
 * @returns
 */
async function putToY4Api(servlet, command, params, queries, config = null) {
  try {
    var oqs = getSearchFromQueries(queries);
    if (!params) params = {};
    params.path = window.location.pathname;
    const response = await axios.put(
      process.env.REACT_APP_API_ROOT +
        `/Y4/PutToServlet?servlet=${servlet}&command=${command}${oqs}`,
      params,
      config
    );
    return checkY4Result(new RT(response));
  } catch (error) {
    return new RT(error);
  }
}

/**
 * Get data from y4 api by servlet
 * @param {*} command
 * @param {*} params
 * @returns
 */
async function getFromY4Api(servlet, command, params) {
  try {
    if (!params) params = {};
    params.path = window.location.pathname;
    const response = await axios.get(
      process.env.REACT_APP_API_ROOT +
        `/Y4/GetByServlet?servlet=${servlet}&command=${command}`,
      { params }
    );
    return checkY4Result(new RT(response));
  } catch (error) {
    return new RT(error);
  }
}

/**
 *
 * @param {*} user
 * @param {*} password
 */
async function login(team, corp, type, user, password) {
  try {
    const response = await axios.post(
      process.env.REACT_APP_API_ROOT + `/Y4/Login`,
      {
        team,
        corp,
        type,
        user,
        password,
        system: process.env.REACT_APP_Y4_SUB_SYSTEM,
        path: window.location.pathname,
        guid: uuidv4()
      }
    );
    return checkY4Result(new RT(response));
  } catch (error) {
    return new RT(error);
  }
}

async function forgetPassword(team, corp, type, user) {
  try {
    const response = await axios.post(
      process.env.REACT_APP_API_ROOT + `/Y4/ForgetPassword`,
      {
        team,
        corp,
        type,
        user,
        system: process.env.REACT_APP_Y4_SUB_SYSTEM
      }
    );
    return checkY4Result(new RT(response));
  } catch (error) {
    return new RT(error);
  }
}

async function getDataDefinition(code, params = {}) {
  return callY4Servlet('WebUiService', 'GetDataDefinition', {
    ...params,
    code //Can't move this line to up. Sometime to overide code in params.
  });
}

function convertApiParams(params) {
  for (const p in params) {
    const v = params[p];
    const v_api = params[`${p}_api`];
    if (v && v_api) {
      delete params[`${p}_api`];
      params[p] = v_api;
    }
  }
}

async function getListByDataDefinition(code, params) {
  convertApiParams(params);
  return callY4Servlet('WebUiService', 'GetListByDataDefinition', {
    ...params,
    code //Can't move this line to up. Sometime to overide code in params.
  });
}

async function getQuoteListByDataDefinition(code, params) {
  convertApiParams(params);
  return callY4Servlet('WebUiService', 'GetQuoteListByDataDefinition', {
    ...params,
    code //Can't move this line to up. Sometime to overide code in params.
  });
}

async function getChartByDataDefinition(code, chart, params) {
  convertApiParams(params);
  return callY4Servlet('WebUiService', 'GetChartByDataDefinition', {
    ...params,
    code, //Can't move this line to up. Sometime to overide code in params.
    chart
  });
}

async function getStatisticsByDataDefinition(code, params) {
  convertApiParams(params);
  return callY4Servlet('WebUiService', 'GetStatisticsByDataDefinition', {
    ...params,
    code //Can't move this line to up. Sometime to overide code in params.
  });
}

async function saveByDataDefinition(code, params) {
  convertApiParams(params);
  return callY4Servlet('WebUiService', 'SaveByDataDefinition', {
    ...params,
    code //Can't move this line to up. Sometime to overide code in params.
  });
}

async function deleteByDataDefinition(code, params) {
  convertApiParams(params);
  return callY4Servlet('WebUiService', 'DeleteByDataDefinition', {
    ...params,
    code //Can't move this line to up. Sometime to overide code in params.
  });
}

async function sqlByDataDefinition(code, params) {
  convertApiParams(params);
  return callY4Servlet('WebUiService', 'SqlByDataDefinition', {
    ...params,
    code //Can't move this line to up. Sometime to overide code in params.
  });
}

function checkY4Result(rt) {
  if (rt.isToReLogin()) {
    clearAuthentication();
  } else {
    if (rt.token) {
      axios.defaults.headers.common['token'] = rt.token;
      y.setSessionItem('a15ad93e144548d3a4d647af17d46cb5_t', rt.token);
    }
  }
  return rt;
}

async function downloadFileFromY4Api(servlet, command, params) {
  try {
    var ps = {
      servlet: servlet,
      command: command,
      path: window.location.pathname,
      args: [JSON.stringify(params), 'simple']
    };
    const response = await axios.post(
      process.env.REACT_APP_API_ROOT + '/Y4/CallServletForFile',
      ps,
      { responseType: 'blob' }
    );
    if (response.headers['content-type'] == 'application/octet-stream') {
      const contentDisposition = response.headers['content-disposition'] + ';';

      //First get fillName from UTF-8
      let fileName = '';
      const m1 = contentDisposition.match(/filename\*=UTF-8''(.*);/);
      if (m1) fileName = m1[1];
      if (!fileName) {
        fileName = contentDisposition.match(/filename=(.*);/)[1];
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', decodeURI(fileName));
      document.body.appendChild(link);
      link.click();

      return new RT();
    } else {
      //Get result from Blob object
      const t = await response.data.text();
      var jo = JSON.parse(t);
      return checkY4Result(new RT(jo));
    }
  } catch (error) {
    return new RT(error);
  }
}

async function downloadExcelByDataDefinition(params) {
  convertApiParams(params);
  var rt = await downloadFileFromY4Api(
    'WebUiService',
    'DownloadExcelByDataDefinition',
    params
  );
  return rt;
}

async function changePassword(
  team,
  corp,
  type,
  user,
  currentPassword,
  newPassword,
  confirmPassword
) {
  try {
    const response = await axios.post(
      process.env.REACT_APP_API_ROOT + `/Y4/ChangePassWord`,
      {
        team,
        corp,
        type,
        user,
        currentPassword,
        newPassword,
        confirmPassword,
        path: window.location.pathname
      }
    );
    return checkY4Result(new RT(response));
  } catch (error) {
    return new RT(error);
  }
}

async function postToTask(task, params) {
  return await postToY4Api(
    'Task',
    'Run',
    {
      ...params,
      task
    },
    { task }
  );
}

export {
  postToY4Api as post,
  putToY4Api as put,
  getFromY4Api as get,
  postToTask,
  login,
  forgetPassword,
  logToY4 as log,
  changePassword,
  getListByDataDefinition as getListByDD,
  getChartByDataDefinition as getChartByDD,
  getQuoteListByDataDefinition as getQuoteByDD,
  getStatisticsByDataDefinition as getStatisticsByDD,
  getDataDefinition as getDD,
  downloadExcelByDataDefinition as downloadExcelByDD,
  saveByDataDefinition as saveByDD,
  deleteByDataDefinition as deleteByDD,
  sqlByDataDefinition as sqlByDD
};
